import React from "react";
import Toolbar from "@mui/material/Toolbar";
import { Box, Button, Grid, Typography } from "@mui/material";
import { useNavigate } from "react-router";
import { Helmet, HelmetProvider } from "react-helmet-async";
import Footer from "../Components/Footer/Footer";
import MenuCard from "../Components/MenuCards/MenuCards";

import VeganPorkMeatballs from "../Assets/vegan_pork_meatballs.png";
import VeganPateMetloaf from "../Assets/vegan_pate_meatloaf.png";
import VeganGrilledPork from "../Assets/vegan_grilled_pork.png";
import VeganSpecialCombo from "../Assets/vegan_special_combo.png";
import VeganShreddedPorkSkin from "../Assets/vegan_shredded_pork_skin.png";
import VeganGrilledChicken from "../Assets/vegan_grilled_chicken.png";
import VeganLemongrassSteak from "../Assets/vegan_lemongrass_steak.png";
import VeganFriedChicken from "../Assets/vegan_ga_chien.png";

import useMetaTracking from "../hooks/useMetaTracking";

const ORDER_URL = "https://order.toasttab.com/online/dong-hung-vien";

const POPULAR_BADGE = { badge: "POPULAR PICKS", badgeBackground: "#00aae3" };
const CLASSIC_BADGE = { badge: "CLASSIC", badgeBackground: "#C2255C" };

function VeganBanhMi() {
  const navigate = useNavigate();

  useMetaTracking("ViewContent", {
    content_name: "Vegan Bánh Mì Page",
    content_category: "Vegan Menu",
  });

  const items = [
    { img: VeganSpecialCombo, title: "Vegan Special Combination – $9.50", subTitle: "Đặc Biệt Chay", path: "/vegan-special-combo", ...POPULAR_BADGE },
    { img: VeganLemongrassSteak, title: "Vegan Lemongrass Steak – $10.00", subTitle: "Bít Tết Sả Chay", path: "/vegan-lemongrass-steak", ...POPULAR_BADGE },
    { img: VeganFriedChicken, title: "Vegan Fried Chicken – $10.50", subTitle: "Gà Chiên Chay", path: "/vegan-fried-chicken", ...POPULAR_BADGE },
    { img: VeganGrilledPork, title: "Vegan Grilled Pork – $9.00", subTitle: "Thịt Nướng Chay", path: "/vegan-grilled-pork", ...CLASSIC_BADGE },
    { img: VeganGrilledChicken, title: "Vegan Grilled Chicken – $9.00", subTitle: "Gà Nướng Chay", path: "/vegan-grilled-chicken", ...CLASSIC_BADGE },
    { img: VeganPorkMeatballs, title: "Vegan Pork Meatballs – $8.50", subTitle: "Xíu Mại Chay", path: "/vegan-pork-meatballs" },
    { img: VeganPateMetloaf, title: "Vegan Pâté Meat Loaf – $8.50", subTitle: "Pâté Chả Lụa Chay", path: "/vegan-pate-meatloaf" },
    { img: VeganShreddedPorkSkin, title: "Vegan Shredded Pork Skin – $8.50", subTitle: "Bì Chay", path: "/vegan-shredded-pork-skin" },
  ];

  return (
    <HelmetProvider>
      {/* push below fixed AppBar */}
      <Toolbar />

      <Helmet>
        <title>Vegan Bánh Mì | Đông Hưng Viên Bakery</title>
        <meta
          name="description"
          content="Enjoy our vegan bánh mì with fresh ingredients and authentic Vietnamese flavors. Filling includes your choice of vegan base, vegan pâté spread, pickled daikon and carrots, cucumber, cilantro, jalapeños, and soy sauce."
        />
        <meta property="og:title" content="Vegan Bánh Mì | Đông Hưng Viên Bakery" />
        <meta
          property="og:description"
          content="Enjoy our vegan bánh mì with fresh ingredients and authentic Vietnamese flavors."
        />
      </Helmet>

      <Box sx={{ display: "flex", justifyContent: "center", background: "#000", py: 10 }}>
        <Box sx={{ maxWidth: "1080px", width: "90%" }}>
          <Box
            sx={{
              px: { xs: 2, md: 6 },
              py: { xs: 4, md: 5 },
              mb: 5,
            }}
          >
            <Typography
              component="h1"
              sx={{
                color: "#fff",
                fontWeight: 800,
                textAlign: "center",
                textTransform: "uppercase",
                letterSpacing: "-0.02em",
                lineHeight: 1.15,
                fontSize: { xs: "clamp(28px, 6vw, 36px)", md: "clamp(32px, 4vw, 42px)" },
                mb: 2,
              }}
            >
              Vegan Bánh Mì
            </Typography>

            <Box sx={{ borderBottom: "1px solid rgba(255,255,255,0.35)", mb: 3 }} />

            <Typography
              sx={{
                color: "#fff",
                textAlign: "center",
                maxWidth: 820,
                mx: "auto",
                lineHeight: 1.7,
                fontSize: { xs: 15, md: 16 },
                opacity: 0.95,
                mb: 3,
              }}
            >
              Fresh Vietnamese baguettes, house-made vegan fillings, crisp pickles, herbs, and jalapeños.
              <br />
              Choose from 8 vegan bánh mì, made fresh in-house.
            </Typography>

            <Box sx={{ display: "flex", justifyContent: "center" }}>
              <Button
                component="a"
                href={ORDER_URL}
                target="_blank"
                rel="noopener noreferrer"
                variant="contained"
                sx={{
                  backgroundColor: "#00aae3",
                  color: "#fff",
                  borderRadius: "999px",
                  px: 3,
                  py: 1,
                  fontWeight: 700,
                  textTransform: "uppercase",
                  boxShadow: "none",
                  "&:hover": { backgroundColor: "#33BBE8", boxShadow: "none" },
                }}
              >
                Order Vegan Bánh Mì
              </Button>
            </Box>

            <Box sx={{ borderBottom: "1px solid rgba(255,255,255,0.35)", mt: 4 }} />
          </Box>

          <Grid container spacing={4}>
            {items.map((item, index) => (
              <Grid
                item
                lg={4}
                md={6}
                sm={6}
                xs={12}
                key={index}
                sx={{ display: "flex", justifyContent: "center" }}
              >
                <MenuCard
                  img={item.img}
                  title={item.title}
                  subTitle={item.subTitle}
                  badge={item.badge}
                  badgeBackground={item.badgeBackground}
                  contentBackground="#283341"
                  contentSecondaryColor="#e0af4a"
                  onAddToCart={() => navigate(item.path)}
                />
              </Grid>
            ))}
          </Grid>
        </Box>
      </Box>

      <Footer />
    </HelmetProvider>
  );
}

export default VeganBanhMi;